// lib/koi-pond-v3/behaviors/koiConvergence.ts
//
// T10.4 — occasional koi convergence. Every 2–4 minutes the koi briefly
// drift toward a single shared point, hover loosely for a few seconds,
// then disperse. Never the same point twice in a row.
//
// This controller owns the schedule + the point; it returns a pull
// {x, z, strength} each frame that PondSceneV3 hands to every swimmer.
// The swimmer steers toward (x,z) scaled by strength; the existing
// separation steering keeps the koi from stacking, so they mill loosely
// around the point. strength ramps up (gather), holds (hover), ramps down
// (disperse).

const ACTIVE_MIN = 7; // total gather+hover+disperse window
const ACTIVE_MAX = 10;
const RAMP_IN = 2;
const RAMP_OUT = 2.5;

// pick within the moonlit-band region the koi already favour, kept central
// enough that the soft boundary doesn't fight the gather.
const CENTER_X = 1.2;
const CENTER_Z = 0.9;
const SPREAD_X = 1.1;
const SPREAD_Z = 0.8;

const rand = (a: number, b: number) => a + Math.random() * (b - a);
const smoothstep = (x: number) => {
  const t = Math.min(1, Math.max(0, x));
  return t * t * (3 - 2 * t);
};

export interface ConvergePull {
  x: number;
  z: number;
  strength: number;
}

export function makeKoiConvergence(fast = false) {
  const GAP_MIN = fast ? 7 : 120; // 2 min in prod
  const GAP_MAX = fast ? 10 : 240; // 4 min in prod
  let active = false;
  let nextStart = rand(GAP_MIN, GAP_MAX) * 0.4; // first one sooner than a full gap
  let started = -1;
  let dur = 0;
  let x = CENTER_X;
  let z = CENTER_Z;
  let lastX = x;
  let lastZ = z;

  function pickPoint() {
    // choose a fresh point at least ~1.2 units from the previous one
    for (let i = 0; i < 8; i++) {
      const nx = CENTER_X + rand(-SPREAD_X, SPREAD_X);
      const nz = CENTER_Z + rand(-SPREAD_Z, SPREAD_Z);
      if (Math.hypot(nx - lastX, nz - lastZ) > 1.2) {
        x = nx; z = nz; break;
      }
      x = nx; z = nz;
    }
    lastX = x; lastZ = z;
  }

  function update(t: number): ConvergePull {
    if (!active) {
      if (t >= nextStart) {
        active = true;
        started = t;
        dur = rand(ACTIVE_MIN, ACTIVE_MAX);
        pickPoint();
      }
      return { x, z, strength: 0 };
    }
    const e = t - started;
    if (e >= dur) {
      active = false;
      nextStart = t + rand(GAP_MIN, GAP_MAX);
      return { x, z, strength: 0 };
    }
    // envelope: ramp in → hold → ramp out
    let strength = 1;
    if (e < RAMP_IN) strength = smoothstep(e / RAMP_IN);
    else if (e > dur - RAMP_OUT) strength = smoothstep((dur - e) / RAMP_OUT);
    return { x, z, strength };
  }

  return { update };
}
